import { useState, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (documentId: number) => void;
}

const DocumentSigningCreateDrawer: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { documentSigningApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [signingOrder, setSigningOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = intl.get('validation.required') || 'Required';
    if (!file) e.file = intl.get('validation.required') || 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const createRes = await documentSigningApiWrapper.createDocument('1.0', { title, description });
      const documentId: number = createRes.data?.data?.id;

      if (file) {
        await documentSigningApiWrapper.uploadDocument('1.0', documentId, file);
      }

      toast.success({ message: intl.get('documentSigning.createSuccess') || 'Document created.' });
      onCreated(documentId);
    } catch (err) {
      handleAPIError(err, toast, {});
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setFile(null);
    setSigningOrder(false);
    setErrors({});
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 440, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{intl.get('documentSigning.upload') || 'New Signing Document'}</Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={2}>
          <TextField
            label={intl.get('documentSigning.titleLabel') || 'Title'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            fullWidth
            required
          />

          <TextField
            label={intl.get('documentSigning.descriptionLabel') || 'Description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {intl.get('documentSigning.documentFile') || 'Document (PDF, DOC, DOCX)'} *
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              size="small"
            >
              {file ? file.name : intl.get('documentSigning.chooseFile') || 'Choose File'}
            </Button>
            {errors.file && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {errors.file}
              </Typography>
            )}
          </Box>

          <FormControlLabel
            control={<Switch checked={signingOrder} onChange={(e) => setSigningOrder(e.target.checked)} />}
            label={intl.get('documentSigning.sequentialSigning') || 'Sequential signing (signers sign in order)'}
          />
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
          <Button onClick={handleClose} disabled={saving}>
            {intl.get('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? (intl.get('common.saving') || 'Saving...') : (intl.get('common.create') || 'Create')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DocumentSigningCreateDrawer;
